/**
 * TicketListReport (LISTS-01, on the universal engine). The deep archive behind
 * the Pending strip's "Recently cleared" peek — every queue ticket ever, now on
 * the SAME engine as Customers/Invoices/Dispatches: pick columns, filter by any
 * field, group (by Lane / Status / City) for counts, save views, CSV. Lane +
 * status keep their plain-English quick-selects (they need label mapping);
 * status stays lane-aware ("Called back" for a cleared missed call).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { CustomerLink } from "@/components/links";
import { cn } from "@/lib/cn";
import { pagedAll } from "@/lib/pagedAll";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { useQueueLanes } from "@/features/board/queue";
import {
  CsvButton, DateRangeControl, ViewsMenu, ListSearch, downloadCsv, pillSelectCls, isoDaysAgo, isoToday,
  useSort, useColumns, ColumnsButton, ListTable, FiltersButton, FilterChips, applyFilters,
  type ListField, type FilterCond,
} from "./listCore";

interface Row {
  id: string;
  created_at: string;
  lane_key: string;
  custno: string | null;
  locno: string | null;
  customer_name: string;
  city: string | null;
  summary: string;
  status: "pending_schedule" | "scheduled" | "cancelled";
  source: string;
  resolved_at: string | null;
  resolved_by: string | null;
}

interface Filters { from: string; to: string; q: string; lane: string; status: string }
const DEFAULT_FILTERS = (): Filters => ({ from: isoDaysAgo(30), to: isoToday(), q: "", lane: "", status: "" });
const COLS = "id, created_at, lane_key, custno, locno, customer_name, city, summary, status, source, resolved_at, resolved_by";
const DISPLAY_CAP = 1500;

const fmtDate = (d: string | null) => { if (!d) return "—"; try { return format(parseISO(d), "MMM d, yyyy"); } catch { return d.slice(0, 10); } };
const fmtShort = (d: string | null) => { if (!d) return "—"; try { return format(parseISO(d), "MMM d"); } catch { return d.slice(0, 10); } };

export function TicketListReport() {
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [conds, setConds] = useState<FilterCond[]>([]);
  const lanes = useQueueLanes().data ?? [];
  const sort = useSort<Row>("created", "desc");
  const laneLabel = (key: string) => lanes.find((l) => l.key === key)?.label ?? key;
  const statusLabel = (r: Row) => r.status === "pending_schedule" ? "Open" : r.status === "cancelled" ? "Cancelled" : (r.lane_key === "missed_call" ? "Called back" : "Scheduled");

  const fields: ListField<Row>[] = useMemo(() => [
    { id: "created", type: "date", filterable: true, col: "created_at", label: "Created", value: (r) => r.created_at, render: (r) => <span className="whitespace-nowrap text-xs text-slate-600">{fmtDate(r.created_at)}</span> },
    { id: "lane", groupable: true, label: "Lane", value: (r) => laneLabel(r.lane_key), render: (r) => <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">{laneLabel(r.lane_key)}</span> },
    { id: "customer", type: "text", filterable: true, col: "customer_name", label: "Customer", value: (r) => displayCustomerName(null, r.customer_name), render: (r) => r.custno
        ? <CustomerLink custno={r.custno} locno={r.locno ?? undefined} className="text-sm">{displayCustomerName(null, r.customer_name)}</CustomerLink>
        : <span className="text-sm text-slate-700">{displayCustomerName(null, r.customer_name)}</span> },
    { id: "summary", type: "text", filterable: true, col: "summary", label: "Summary", value: (r) => r.summary, render: (r) => <span className="block max-w-[280px] truncate text-xs text-slate-600">{r.summary}</span> },
    { id: "status", groupable: true, label: "Status", value: (r) => statusLabel(r), render: (r) => (
        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", r.status === "pending_schedule" && "bg-amber-50 text-amber-800", r.status === "scheduled" && "bg-emerald-50 text-emerald-700", r.status === "cancelled" && "bg-slate-100 text-slate-500")}>{statusLabel(r)}</span>
      ) },
    { id: "cleared_by", type: "text", filterable: true, col: "resolved_by", label: "Cleared by", value: (r) => (r.resolved_by ? r.resolved_by.split("@")[0] : null) },
    { id: "cleared_at", type: "date", filterable: true, col: "resolved_at", label: "Cleared", value: (r) => r.resolved_at, render: (r) => fmtShort(r.resolved_at) },
    { id: "city", type: "text", filterable: true, groupable: true, col: "city", label: "City", value: (r) => r.city, defaultVisible: false },
    { id: "source", type: "text", filterable: true, groupable: true, col: "source", label: "Source", value: (r) => r.source, defaultVisible: false },
    { id: "custno", type: "text", filterable: true, col: "custno", label: "Cust #", value: (r) => r.custno, defaultVisible: false },
  ], [lanes]);
  const cols = useColumns("tickets", fields);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ["list_tickets", f.from, f.to, conds],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Row[]> => pagedAll<Row>(() => {
      let q = supabase.from("pegasus_dispatch").select(COLS).is("deleted_at", null).gte("created_at", `${f.from}T00:00:00`).lte("created_at", `${f.to}T23:59:59`);
      q = applyFilters(q, conds, fields as ListField<unknown>[]);
      return q.order("created_at", { ascending: false });
    }),
  });
  const all = data ?? [];

  const filtered = useMemo(() => {
    const ql = f.q.trim().toLowerCase();
    return all.filter((r) => {
      if (f.lane && r.lane_key !== f.lane) return false;
      if (f.status && r.status !== f.status) return false;
      if (ql) { const hay = [r.customer_name, r.summary, r.city, r.resolved_by, r.custno].join(" ").toLowerCase(); if (!hay.includes(ql)) return false; }
      return true;
    });
  }, [all, f.q, f.lane, f.status]);

  const laneOpts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of all) m.set(r.lane_key, (m.get(r.lane_key) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => laneLabel(a[0]).localeCompare(laneLabel(b[0])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, lanes]);

  const shownRows = filtered.slice(0, DISPLAY_CAP);
  const setPart = (p: Partial<Filters>) => setF((prev) => ({ ...prev, ...p }));

  function exportCsv() {
    const shown = cols.visible.map((id) => fields.find((x) => x.id === id)).filter(Boolean) as ListField<Row>[];
    downloadCsv(`tickets_${f.from}_${f.to}.csv`, shown.map((c) => c.label), filtered.map((r) => shown.map((c) => { const v = c.value(r); return v == null ? "" : String(v); })));
  }

  return (
    <div className="min-w-0 flex-1">
      {/* TOOLBAR-REFRESH — a search-forward command row + a clean scoping row */}
      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <ListSearch value={f.q} onChange={(v) => setPart({ q: v })} placeholder="Search customer, summary, who cleared it…" />
          <FiltersButton fields={fields} conds={conds} onChange={setConds} />
          <ColumnsButton fields={fields} cols={cols} />
          <CsvButton onClick={exportCsv} />
          <ViewsMenu entity="tickets" currentFilters={f as unknown as Record<string, unknown>} onApply={(saved) => setF({ ...DEFAULT_FILTERS(), ...(saved as Partial<Filters>) })} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangeControl from={f.from} to={f.to} onChange={(from, to) => setF((prev) => ({ ...prev, from, to }))} />
          <select value={f.lane} onChange={(e) => setPart({ lane: e.target.value })} className={pillSelectCls} title="Filter by lane">
            <option value="">All lanes</option>
            {laneOpts.map(([k, n]) => <option key={k} value={k}>{laneLabel(k)} ({n})</option>)}
          </select>
          <select value={f.status} onChange={(e) => setPart({ status: e.target.value })} className={pillSelectCls} title="Filter by status">
            <option value="">All statuses</option><option value="pending_schedule">Open</option><option value="scheduled">Cleared</option><option value="cancelled">Cancelled</option>
          </select>
          {conds.length > 0 && <FilterChips conds={conds} fields={fields} onChange={setConds} />}
          <div className="ml-auto whitespace-nowrap text-sm tabular-nums text-slate-500"><strong className="text-slate-800">{filtered.length.toLocaleString()}</strong> tickets{isFetching ? " · …" : ""}</div>
        </div>
      </div>

      {isLoading ? (
        <Card className="h-40 animate-pulse bg-slate-100" />
      ) : isError ? (
        <Card className="p-6"><p className="text-sm text-red-600">{(error as Error)?.message}</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
            <span className="text-[14.5px] font-bold tracking-tight text-slate-900">Ticket History</span>
            <InfoTip label="About this list">Every queue ticket (incoming, parts, return visits, missed calls) with who cleared it and when. For counts, group by Lane or Status in the Fields menu.</InfoTip>
            <span className="ml-auto text-xs tabular-nums text-slate-500">{filtered.length.toLocaleString()} tickets{filtered.length > DISPLAY_CAP ? ` · showing first ${DISPLAY_CAP.toLocaleString()}` : ""}</span>
          </div>
          <div className="max-h-[68vh] overflow-auto">
            <ListTable rows={shownRows} fields={fields} cols={cols} sort={sort} getKey={(r) => r.id} emptyText="Nothing matches — widen the dates or clear a filter." />
          </div>
        </Card>
      )}
    </div>
  );
}
